import { useState, useEffect, useRef } from "react";
import type { OutlookShipmentFeed, GetShipmentFeedResponse } from "@services/demo/msoffice/types";

export interface UseShipmentFeedOptions {
  fetchShipmentFeed: (messageId: string) => Promise<GetShipmentFeedResponse>;
}

export function useShipmentFeed(
  messageId: string | null,
  options: UseShipmentFeedOptions
) {
  const [shipment, setShipment] = useState<OutlookShipmentFeed | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use ref to store the fetch function to avoid infinite loops
  // when options object is recreated on each render
  const fetchShipmentFeedRef = useRef(options.fetchShipmentFeed);
  fetchShipmentFeedRef.current = options.fetchShipmentFeed;

  useEffect(() => {
    if (!messageId) {
      setShipment(null);
      setLoading(false);
      setError(null);
      return;
    }

    const fetchShipment = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetchShipmentFeedRef.current(messageId);

        // Assuming either 0 or 1 shipment as mentioned
        if (response.relatedShipments.length > 0) {
          setShipment(response.relatedShipments[0]);
        } else {
          setShipment(null);
        }
      } catch (err) {
        console.error("Error fetching shipment feed:", err);
        setError("Failed to load shipment information");
        setShipment(null);
      } finally {
        setLoading(false);
      }
    };

    fetchShipment();
  }, [messageId]);

  return {
    shipment,
    loading,
    error,
    hasShipment: shipment !== null,
  };
}
